import React, { useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup"; // Import Yup for validation
import AuthContent from "./AuthContent";
import { userLogin } from "../../Common/data/userLogin"; // Import the userLogin API function
import logolight from "../../assets/images/logo-light.png";
import Cookies from "js-cookie"; // Import js-cookie

const SignIn = () => {
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // State for loading spinner
    const [error, setError] = useState<string | null>(null); // State for API error
    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        email: Yup.string().email("Invalid email address").required("Email is required"),
        password: Yup.string().required("Password is required"),
    });

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true); // Start loading spinner
                // Call the userLogin API function
                const response = await userLogin(values.email, values.password);
                console.log("Response from API:", response); // Log the response

                if (response.status === "success" && response.token) {
                    const userData = response.data.user;
                    const token = response.token;

                    // Store user data and token in cookies
                    Cookies.set("userData", JSON.stringify(userData), { expires: 7 }); // Store user data in cookies (7 days)
                    Cookies.set("authToken", token, { expires: 7 }); // Store token in cookies (7 days)

                    console.log("Token stored:", Cookies.get("authToken"));

                    navigate("/dashboard"); // Redirect to dashboard on successful login
                } else {
                    setError(response.message); // Set API error message
                }
            } catch (error: any) {
                console.error("Error logging in user:", error.response?.data.message || error.message);
                if (error.response && error.response.status === 401) {
                    setError(error.response.data.message || "Login failed"); // Set API error message for 401
                } else {
                    setError("An unexpected error occurred. Please try again."); // Handle other errors
                }
            } finally {
                setIsLoading(false); // Stop loading spinner
            }
        },
    });

    const handleTogglePassword = () => {
        setPasswordVisible((prevVisible) => !prevVisible);
    };

    return (
        <React.Fragment>
            <section className="position-relative min-vh-100 d-flex align-items-center justify-content-center">
                <Container fluid>
                    <Row className="justify-content-center">
                        {window.innerWidth > 768 ? <AuthContent /> : null}
                        <Col xs={12} md={6}>
                            <div className="auth-card">
                                <Card className="border-0 mb-0">
                                    <Card.Body className="px-md-5">
                                        <img src={logolight} height={100} />
                                        <h2 className="mb-4">Sign In</h2>
                                        {error && <Alert variant="danger">{error}</Alert>}
                                        <Form onSubmit={formik.handleSubmit}>
                                            <Form.Group controlId="email" className="mb-3">
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="Enter your email"
                                                    value={formik.values.email}
                                                    onChange={formik.handleChange}
                                                    isInvalid={formik.touched.email && !!formik.errors.email}
                                                />
                                                <Form.Control.Feedback type="invalid">{formik.errors.email}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group controlId="password" className="mb-3 position-relative">
                                                <Form.Label>Password</Form.Label>
                                                <div className="password-toggle">
                                                    <Form.Control
                                                        type={passwordVisible ? "text" : "password"}
                                                        placeholder="Enter your password"
                                                        value={formik.values.password}
                                                        onChange={formik.handleChange}
                                                        isInvalid={formik.touched.password && !!formik.errors.password}
                                                    />
                                                    <Button
                                                        className="btn btn-link position-absolute end-0 bottom-0 text-decoration-none text-muted password-addon"
                                                        onClick={handleTogglePassword}
                                                        variant="light"
                                                    >
                                                        <i className={passwordVisible ? "ri-eye-fill align-middle" : "ri-eye-off-fill align-middle"} />
                                                    </Button>
                                                </div>
                                                <Form.Control.Feedback type="invalid">{formik.errors.password}</Form.Control.Feedback>
                                            </Form.Group>
                                            <div className="mb-3">
                                                <Link to="/forgot-password">Forgot Password?</Link>
                                            </div>
                                            <Button variant="primary" type="submit" className="w-100" disabled={isLoading}>
                                                {isLoading ? <Spinner animation="border" size="sm" role="status" /> : "Sign In"}
                                            </Button>
                                        </Form>
                                        <div className="mt-3 text-center">
                                            <p>New user? <Link to="/create-account">Create Account</Link></p>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default SignIn;
